import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, Auth } from 'aws-amplify';
import {
  Button,
  Container,
  ContentLayout,
  Header,
  Table,
  SpaceBetween,
  Modal,
  Alert,
  Spinner,
  BreadcrumbGroup,
  Autosuggest,
  AutosuggestProps,
  FormField,
  Flashbar,
  FlashbarProps,
  RadioGroup
} from '@cloudscape-design/components';
import { Team, User } from '../api-types';
import UserContext from '../contexts/UserContext';

interface TeamManagementProps {
  teamId?: string;
}

interface UserWithSelection extends User {
  selected?: boolean;
}

const TeamManagement: React.FC<TeamManagementProps> = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const user = useContext(UserContext);

  const [team, setTeam] = useState<Team | null>(null);
  const [teamUsers, setTeamUsers] = useState<UserWithSelection[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserWithSelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTransferWarning, setShowTransferWarning] = useState(false);
  const [usersToAdd, setUsersToAdd] = useState<User[]>([]);
  const [usersWithWarnings, setUsersWithWarnings] = useState<User[]>([]);
  const [flashMessages, setFlashMessages] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [autosuggestValue, setAutosuggestValue] = useState('');
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<User[]>([]);
  const [editingRoles, setEditingRoles] = useState<{[userId: string]: boolean}>({});
  const [pendingRoleChanges, setPendingRoleChanges] = useState<{[userId: string]: {isEm: boolean}}>({});

  // Check if current user can manage this team
  const canManageTeam = () => {
    if (!team || !user) return false;
    const groups = user.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];
    return team.managerId === user.attributes?.preferred_username || groups.includes('admins');
  };

  // Load team data
  useEffect(() => {
    if (!teamId) return;

    const loadTeamData = async () => {
      try {
        setLoading(true);
        const session = await Auth.currentSession();

        // Get team details with users
        const teamQuery = `{
          getTeam(teamId: "${teamId}") {
            teamId
            name
            managerId
            users {
              userId
              name
              isManager
              isEm
              managerId
            }
          }
        }`;

        const { data } = await API.graphql<any>({
          query: teamQuery
        }, {
          Authorization: session.getIdToken().getJwtToken()
        });

        if (data.getTeam) {
          setTeam(data.getTeam);
          const sortedUsers = (data.getTeam.users || []).sort((a: User, b: User) =>
            (a.name || a.userId || '').localeCompare(b.name || b.userId || '')
          );
          setTeamUsers(sortedUsers);
        }

        // Get available users from all teams (for transfer warnings and selection)
        const usersQuery = `{
          listTeams {
            teamId
            name
            users {
              userId
              name
              isManager
              isEm
              managerId
            }
          }
          getTeam(teamId: "aws") {
            teamId
            name
            users {
              userId
              name
              isManager
              isEm
              managerId
            }
          }
        }`;

        const usersResponse = await API.graphql<any>({
          query: usersQuery
        }, {
          Authorization: session.getIdToken().getJwtToken()
        });

        // Extract users from all teams (including AWS pool) and add team info
        const regularTeamUsers = usersResponse.data.listTeams
          ?.flatMap((team: Team) =>
            (team.users || []).map((user: User) => ({
              ...user,
              team: { teamId: team.teamId, name: team.name }
            }))
          ) || [];

        const awsPoolUsers = usersResponse.data.getTeam?.users?.map((user: User) => ({
          ...user,
          team: { teamId: 'aws', name: 'AWS Pool' }
        })) || [];

        const allUsers = [...regularTeamUsers, ...awsPoolUsers]
          .filter((u: User) => !data.getTeam.users?.some((tu: User) => tu.userId === u.userId)) // Exclude current team users
          .filter((u: User) => !u.isManager) // Exclude managers from being moved
          .sort((a, b) => (a.name || a.userId || '').localeCompare(b.name || b.userId || '')); // Sort by name ascending

        setAvailableUsers(allUsers);

      } catch (error) {
        console.error('Error loading team data:', error);
        setFlashMessages([{
          type: 'error',
          content: 'Failed to load team data',
          dismissible: true
        }]);
      } finally {
        setLoading(false);
      }
    };

    loadTeamData();
  }, [teamId]);

  // Handle user selection in table
  const handleSelectionChange = (selectedItems: UserWithSelection[]) => {
    setSelectedUsers(selectedItems);
  };

  // Handle adding a user from autosuggest
  const handleAddUserFromAutosuggest = (option: AutosuggestProps.Option) => {
    const userToAdd = availableUsers.find(u => u.userId === option.value);
    if (userToAdd && !selectedUsersToAdd.some(u => u.userId === userToAdd.userId)) {
      setSelectedUsersToAdd([...selectedUsersToAdd, userToAdd]);
      setAutosuggestValue('');
    }
  };

  // Handle removing a selected user
  const handleRemoveSelectedUser = (userId: string) => {
    setSelectedUsersToAdd(selectedUsersToAdd.filter(u => u.userId !== userId));
  };

  // Handle confirming the add users action
  const handleConfirmAddUsers = () => {
    if (selectedUsersToAdd.length === 0) return;

    // Check for users that need transfer warnings (not in 'aws' pool)
    const usersNeedingWarning = selectedUsersToAdd.filter(u =>
      u.team?.teamId && u.team.teamId !== 'aws'
    );

    if (usersNeedingWarning.length > 0) {
      setUsersWithWarnings(usersNeedingWarning);
      setUsersToAdd(selectedUsersToAdd);
      setShowTransferWarning(true);
    } else {
      // Proceed directly with adding users
      executeAddUsers(selectedUsersToAdd);
    }
  };

  // Handle role editing
  const handleStartRoleEdit = (userId: string) => {
    setEditingRoles({ ...editingRoles, [userId]: true });
  };

  const handleCancelRoleEdit = (userId: string) => {
    const newEditingRoles = { ...editingRoles };
    delete newEditingRoles[userId];
    setEditingRoles(newEditingRoles);

    const newPendingChanges = { ...pendingRoleChanges };
    delete newPendingChanges[userId];
    setPendingRoleChanges(newPendingChanges);
  };

  const handleRoleChange = (userId: string, newRole: string) => {
    setPendingRoleChanges({
      ...pendingRoleChanges,
      [userId]: { isEm: newRole === 'EM' }
    });
  };

  const handleSaveRoleChange = async (userId: string) => {
    const roleChange = pendingRoleChanges[userId];
    if (!roleChange) return;

    try {
      const session = await Auth.currentSession();
      const mutation = `
        mutation UpdateTeam($teamId: ID!, $updates: UpdateTeamInput!) {
          updateTeam(teamId: $teamId, updates: $updates) {
            teamId
            name
            managerId
            users {
              userId
              name
              isManager
              isEm
              managerId
            }
          }
        }
      `;

      const variables = {
        teamId: teamId,
        updates: {
          usersToUpdate: [{
            userId: userId,
            isEm: roleChange.isEm
          }]
        }
      };

      const { data } = await API.graphql<any>({
        query: mutation,
        variables
      }, {
        Authorization: session.getIdToken().getJwtToken()
      });

      if (data.updateTeam) {
        setTeam(data.updateTeam);
        setTeamUsers(data.updateTeam.users || []);
        setFlashMessages([{
          type: 'success',
          content: 'Successfully updated user role',
          dismissible: true
        }]);

        // Clear editing state
        handleCancelRoleEdit(userId);
      }

    } catch (error) {
      console.error('Error updating user role:', error);
      setFlashMessages([{
        type: 'error',
        content: 'Failed to update user role',
        dismissible: true
      }]);
    }
  };

  // Execute the add users mutation
  const executeAddUsers = async (users: User[]) => {
    try {
      const session = await Auth.currentSession();
      const mutation = `
        mutation UpdateTeam($teamId: ID!, $updates: UpdateTeamInput!) {
          updateTeam(teamId: $teamId, updates: $updates) {
            teamId
            name
            managerId
            users {
              userId
              name
              isManager
              managerId
            }
          }
        }
      `;

      const variables = {
        teamId: teamId,
        updates: {
          usersToAdd: users.map(u => ({ userId: u.userId }))
        }
      };

      const { data } = await API.graphql<any>({
        query: mutation,
        variables
      }, {
        Authorization: session.getIdToken().getJwtToken()
      });

      if (data.updateTeam) {
        setTeam(data.updateTeam);
        setTeamUsers(data.updateTeam.users || []);
        setFlashMessages([{
          type: 'success',
          content: `Successfully added ${users.length} user(s) to the team`,
          dismissible: true
        }]);
      }

    } catch (error) {
      console.error('Error adding users:', error);
      setFlashMessages([{
        type: 'error',
        content: 'Failed to add users to team',
        dismissible: true
      }]);
    } finally {
      setShowAddModal(false);
      setShowTransferWarning(false);
      setSelectedUsersToAdd([]);
      setAutosuggestValue('');
    }
  };

  // Handle remove users
  const handleRemoveUsers = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const session = await Auth.currentSession();
      const mutation = `
        mutation UpdateTeam($teamId: ID!, $updates: UpdateTeamInput!) {
          updateTeam(teamId: $teamId, updates: $updates) {
            teamId
            name
            managerId
            users {
              userId
              name
              isManager
              managerId
            }
          }
        }
      `;

      const variables = {
        teamId: teamId,
        updates: {
          usersToRemove: selectedUsers.map(u => ({ userId: u.userId }))
        }
      };

      const { data } = await API.graphql<any>({
        query: mutation,
        variables
      }, {
        Authorization: session.getIdToken().getJwtToken()
      });

      if (data.updateTeam) {
        setTeam(data.updateTeam);
        setTeamUsers(data.updateTeam.users || []);
        setSelectedUsers([]);
        setFlashMessages([{
          type: 'success',
          content: `Successfully removed ${selectedUsers.length} user(s) from the team`,
          dismissible: true
        }]);
      }

    } catch (error) {
      console.error('Error removing users:', error);
      setFlashMessages([{
        type: 'error',
        content: 'Failed to remove users from team',
        dismissible: true
      }]);
    }
  };

  if (loading) {
    return (
      <ContentLayout>
        <Container>
          <Spinner size="large" />
        </Container>
      </ContentLayout>
    );
  }

  if (!team) {
    return (
      <ContentLayout>
        <Container>
          <Alert type="error">Team not found</Alert>
        </Container>
      </ContentLayout>
    );
  }

  if (!canManageTeam()) {
    return (
      <ContentLayout>
        <Container>
          <Alert type="error">You don't have permission to manage this team</Alert>
        </Container>
      </ContentLayout>
    );
  }

  const availableUserOptions = availableUsers
    .filter(u => !teamUsers.some(tu => tu.userId === u.userId))
    .map(u => ({
      label: `${u.name} (${u.team?.name || 'AWS Pool'})`,
      value: u.userId!
    }));

  return (
    <ContentLayout
      className="layout"
      header={
        <Header
          variant='h2'
        >
          Edit Team: {team.name}
          <BreadcrumbGroup
            items={[
              { text: 'Teams', href: '/teams' },
              { text: team.name || 'Team Management', href: '#' }
            ]}
          />
          {flashMessages.length > 0 && (
              <Flashbar
                items={flashMessages.map((message, index) => ({
                  ...message,
                  onDismiss: () => {
                    const newMessages = [...flashMessages];
                    newMessages.splice(index, 1);
                    setFlashMessages(newMessages);
                  }
                }))}
              />
            )}
        </Header>
      }
    >

    <Table
        columnDefinitions={[
          {
            id: 'name',
            header: 'Name',
            cell: (item: UserWithSelection) => item.name || item.userId
          },
          {
            id: 'userId',
            header: 'User ID',
            cell: (item: UserWithSelection) => item.userId
          },
          {
            id: 'role',
            header: 'Role',
            cell: (item: UserWithSelection) => {
              // Managers can't change their role
              if (item.isManager) return 'Manager';

              const isEditing = editingRoles[item.userId!];
              const pendingChange = pendingRoleChanges[item.userId!];
              const currentRole = pendingChange ? (pendingChange.isEm ? 'EM' : 'PA') : (item.isEm ? 'EM' : 'PA');

              if (isEditing) {
                return (
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <RadioGroup
                        value={currentRole}
                        onChange={({ detail }) => handleRoleChange(item.userId!, detail.value)}
                        items={[
                          { value: 'EM', label: 'EM' },
                          { value: 'PA', label: 'PA' }
                        ]}
                      />
                    </div>
                    <Button
                      variant="icon"
                      iconName="check"
                      onClick={() => handleSaveRoleChange(item.userId!)}
                    />
                    <Button
                      variant="icon"
                      iconName="close"
                      onClick={() => handleCancelRoleEdit(item.userId!)}
                    />
                  </SpaceBetween>
                );
              }

              return (
                <SpaceBetween direction="horizontal" size="xs">
                  <span>{currentRole}</span>
                  <Button
                    variant="icon"
                    iconName="edit"
                    onClick={() => handleStartRoleEdit(item.userId!)}
                  />
                </SpaceBetween>
              );
            }
          }
        ]}
        items={teamUsers}
        selectionType="multi"
        selectedItems={selectedUsers}
        onSelectionChange={({ detail }) => handleSelectionChange(detail.selectedItems)}
        header={
          <Header
            counter={`(${teamUsers.length})`}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button
                  onClick={() => setShowAddModal(true)}
                  iconName="add-plus"
                >
                  Add Users
                </Button>
                <Button
                  onClick={handleRemoveUsers}
                  disabled={selectedUsers.length === 0}
                  iconName="remove"
                >
                  Remove Selected
                </Button>
              </SpaceBetween>
            }
          >
            Team Members
          </Header>
        }
        empty={
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>No team members found</p>
          </div>
        }
      />

      {/* Add Users Modal */}
      <Modal
        visible={showAddModal}
        onDismiss={() => {
          setShowAddModal(false);
          setSelectedUsersToAdd([]);
          setAutosuggestValue('');
        }}
        header="Add Users to Team"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => {
              setShowAddModal(false);
              setSelectedUsersToAdd([]);
              setAutosuggestValue('');
            }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmAddUsers}
              disabled={selectedUsersToAdd.length === 0}
            >
              Add Users ({selectedUsersToAdd.length})
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <FormField label="Search and select users to add">
            <Autosuggest
              onChange={({ detail }) => setAutosuggestValue(detail.value)}
              onSelect={({ detail }) => {
                if (detail.selectedOption) {
                  handleAddUserFromAutosuggest(detail.selectedOption);
                }
              }}
              value={autosuggestValue}
              options={availableUserOptions}
              placeholder="Type to search for users..."
              empty="No users found"
            />
          </FormField>

          {selectedUsersToAdd.length > 0 && (
            <FormField label={`Selected users (${selectedUsersToAdd.length})`}>
              <SpaceBetween direction="vertical" size="xs">
                {selectedUsersToAdd.map(user => (
                  <div key={user.userId} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px',
                    backgroundColor: '#f2f3f3',
                    borderRadius: '4px'
                  }}>
                    <span>{user.name} ({user.team?.name || 'AWS Pool'})</span>
                    <Button
                      variant="icon"
                      iconName="close"
                      onClick={() => handleRemoveSelectedUser(user.userId!)}
                    />
                  </div>
                ))}
              </SpaceBetween>
            </FormField>
          )}
        </SpaceBetween>
      </Modal>

      {/* Transfer Warning Modal */}
      <Modal
        visible={showTransferWarning}
        onDismiss={() => setShowTransferWarning(false)}
        header="Confirm User Transfer"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowTransferWarning(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => executeAddUsers(usersToAdd)}
            >
              Confirm Transfer
            </Button>
          </SpaceBetween>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <Alert type="warning">
            The following users are currently in other teams and will be transferred:
          </Alert>
          {usersWithWarnings.map(user => (
            <div key={user.userId}>
              <strong>{user.name}</strong> (currently in {user.team?.name})
            </div>
          ))}
        </SpaceBetween>
      </Modal>
    </ContentLayout>
  );
};

export default TeamManagement;